'use client'

import { useMemo } from 'react'
import dynamic from 'next/dynamic'
import type { ListingSummary } from '@/lib/feedbridge/types'
import { formatAddress, generateListingPath } from '@/lib/feedbridge/helpers'

// Dynamically import the map to avoid SSR issues with Leaflet
const ListingMapInner = dynamic(() => import('./ListingMapInner'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-[65vh] bg-tertiary flex items-center justify-center border border-border">
      <div className="flex items-center gap-2 text-body-sm text-muted-foreground">
        <div className="w-4 h-4 border-2 border-border border-t-muted-foreground rounded-full animate-spin" />
        Loading map...
      </div>
    </div>
  ),
})

export interface MapListing {
  id: string
  externalId: string
  lat: number
  lng: number
  address: string
  suburb: string
  price: string | null
  beds: number | null
  baths: number | null
  cars: number | null
  thumbnail: string | null
  slug: string
  status: string | null
  underOffer: boolean
  isOffMarket: boolean
  suburbName: string
}

interface ListingMapViewProps {
  listings: ListingSummary[]
  featuresMap: Record<string, Record<string, unknown>>
  underOfferMap: Record<string, boolean>
  thumbnailMap: Record<string, string>
  coordinatesMap: Record<string, { lat: number; lng: number }>
}

function getCardPrice(listing: ListingSummary): string | null {
  if (listing.listing_status === 'leased') return 'Leased'
  if (listing.listing_status === 'sold') return listing.price_display || 'Sold'
  if (listing.listing_vertical === 'rental') {
    const rent =
      listing.rent_display && !/^(yes|no|true|false)$/i.test(listing.rent_display)
        ? listing.rent_display
        : null
    return rent || 'Contact Agent'
  }
  const display =
    listing.price_display && !/^(yes|no|true|false)$/i.test(listing.price_display)
      ? listing.price_display
      : null
  return display || listing.price_view || 'Contact Agent'
}

function titleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}


export function ListingMapView({
  listings,
  featuresMap,
  underOfferMap,
  thumbnailMap,
  coordinatesMap,
}: ListingMapViewProps) {
  const mapListings = useMemo(() => {
    const result: MapListing[] = []
    for (const listing of listings) {
      const coords = coordinatesMap[listing.external_listing_id]
      if (!coords) continue
      const features = featuresMap[listing.external_listing_id] || {}
      const street = formatAddress(listing).line1
      const suburb = listing.address_suburb ? titleCase(listing.address_suburb) : ''
      result.push({
        id: listing.id,
        externalId: listing.external_listing_id,
        lat: coords.lat,
        lng: coords.lng,
        address: street,
        suburb: [suburb, listing.address_state, listing.address_postcode].filter(Boolean).join(' '),
        price: getCardPrice(listing),
        beds: (features.bedrooms as number) || null,
        baths: (features.bathrooms as number) || null,
        cars: (features.garages as number) || null,
        thumbnail: thumbnailMap[listing.external_listing_id] || listing.primary_image || null,
        // Canonical path (mirror slug, unit included) — the hand-rolled street
        // slug this used to build dropped the unit and could miss the listing.
        slug: generateListingPath(listing).replace(/^\/property\//, ''),
        status: listing.listing_status,
        underOffer: underOfferMap[listing.external_listing_id] || false,
        isOffMarket: listing.listing_status === 'offmarket',
        suburbName: suburb,
      })
    }
    return result
  }, [listings, featuresMap, underOfferMap, thumbnailMap, coordinatesMap])

  if (mapListings.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-muted-foreground text-body">No properties with location data found</p>
      </div>
    )
  }

  return <ListingMapInner listings={mapListings} />
}
